/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Observable, catchError, combineLatestWith, map, of } from 'rxjs'
import { BackendRoutes, LOCALISATION_BASE_URL, LOCALISATION_VERSION } from '../constants'
import { FormType, LocalisationProject } from '../model'

@Injectable({
  providedIn: 'root',
})
export class LocalisationService {
  private readonly localisationBaseUrl = inject(LOCALISATION_BASE_URL)
  private readonly localisationVersion = inject(LOCALISATION_VERSION)
  private readonly http: HttpClient = inject(HttpClient)

  public getNameWidgetConfiguration(country: string, locale: string): Observable<any> {
    const localisationType = 'name'
    return this.getLayoutFor(localisationType, country).pipe(
      combineLatestWith(
        this.getSchemaFor(localisationType, country, locale),
        this.getTranslationFor(this._getProjectByFormType(localisationType), country, locale),
      ),
      map(([layout, schema, translations]) => ({ layout, schema, translations })),
    )
  }

  public getAddressWidgetConfiguration(country: string, locale: string): Observable<any> {
    const localisationType = 'address'
    return this.getLayoutFor(localisationType, country).pipe(
      combineLatestWith(
        this.getSchemaFor(localisationType, country, locale),
        this.getTranslationFor(this._getProjectByFormType(localisationType), country, locale),
      ),
      map(([layout, schema, translations]) => ({ layout, schema, translations })),
    )
  }

  public getBirthdayWidgetConfiguration(country: string, locale: string): Observable<any> {
    return this.getTranslationFor('shared-webcomponents', country, locale).pipe(map((translations) => ({ translations })))
  }

  public getPhoneWidgetConfiguration(country: string, locale: string): Observable<any> {
    return this.getTranslationFor('shared-webcomponents', country, locale).pipe(map((translations) => ({ translations })))
  }

  public getAddSecondaryUserConfiguration(country: string, locale: string): Observable<any> {
    const localisationType = 'add-secondary-user'
    return this.getLayoutFor(localisationType, country).pipe(
      combineLatestWith(this.getSchemaFor(localisationType, country, locale)),
      map(([layout, schema]) => ({ layout, schema })),
    )
  }

  public getLayoutFor(type: FormType, country: string): Observable<any> {
    const configBasePath = BackendRoutes.FORM__LAYOUT.replace(':version', this.localisationVersion)
      .replace(':type', type)
      .replace(':country', country.toLowerCase())

    const configBaseUrl = `${this.localisationBaseUrl}/${configBasePath}`

    return this.http.get(configBaseUrl)
  }

  public getSchemaFor(type: FormType, country: string, locale: string): Observable<any> {
    const [l1, l2] = locale.split('_')
    const updatedLocale = `${l1.toLowerCase()}_${l2.toUpperCase()}`

    const configBasePath = BackendRoutes.FORM__LOCALE_SCHEMA.replace(':version', this.localisationVersion)
      .replace(':type', type)
      .replace(':country', country.toLowerCase())
      .replace(':locale', updatedLocale)

    const configBaseUrl = `${this.localisationBaseUrl}/${configBasePath}`

    return this.http.get(configBaseUrl)
  }

  public getTranslationFor(project: LocalisationProject, country: string, locale: string): Observable<any> {
    const [l1, l2] = locale.split('_')

    const marketSpecificLocale = `${l1.toLowerCase()}_${country.toUpperCase()}`
    const updatedLocale = `${l1.toLowerCase()}_${l2.toUpperCase()}`

    // Market specific translations are stored in a separate bucket
    if (marketSpecificLocale !== updatedLocale) {
      return this.getSpecificTranslationFor(project, marketSpecificLocale).pipe(
        catchError(() => this.getGenericTranslationsFor(project, updatedLocale)),
      )
    }

    return this.getGenericTranslationsFor(project, updatedLocale)
  }

  public getCountryLanguageTranslations(locale: string): Observable<Record<string, string>> {
    const [l1, l2] = locale.split('_')
    const countryLanuageBaseUrl = BackendRoutes.COUNTRY_LANGUAGE__TRANSLATIONS.replace(':version', this.localisationVersion).replace(
      ':locale',
      l1.toLowerCase(),
    )
    const configBaseUrl = `${this.localisationBaseUrl}/${countryLanuageBaseUrl}`
    return this.http.get<Record<string, string>>(configBaseUrl)
  }

  public getCountryTranslations(locale: string): Observable<Record<string, string>> {
    const [l1, l2] = locale.split('_')
    const countryLanuageBaseUrl = BackendRoutes.COUNTRY__TRANSLATIONS.replace(':version', this.localisationVersion).replace(
      ':locale',
      l1.toLowerCase(),
    )
    const configBaseUrl = `${this.localisationBaseUrl}/${countryLanuageBaseUrl}`
    return this.http.get<Record<string, string>>(configBaseUrl)
  }

  private getGenericTranslationsFor(project: LocalisationProject, locale: string): Observable<any> {
    const configBasePath = BackendRoutes.FORM__TRANSLATIONS.replace(':version', this.localisationVersion)
      .replace(':project', project)
      .replace(':locale', `${locale}_generic`)
    const configBaseUrl = `${this.localisationBaseUrl}/${configBasePath}`

    return this.http.get(configBaseUrl)
  }

  private getSpecificTranslationFor(project: LocalisationProject, locale: string): Observable<any> {
    const configBasePath = BackendRoutes.FORM__TRANSLATIONS.replace(':version', this.localisationVersion)
      .replace(':project', project)
      .replace(':locale', locale)
    const configBaseUrl = `${this.localisationBaseUrl}/${configBasePath}`

    return this.http.get(configBaseUrl)
  }

  private _getProjectByFormType(type: FormType): LocalisationProject {
    if (['name', 'address'].includes(type)) {
      return 'shared-webcomponents'
    } else if (
      [
        'signup-prospect-registration',
        'signup-prospect-confirmation',
        'signup-owner-confirmation',
        'signup-organisation-registration',
        'signup-secondary-confirmation',
      ].includes(type)
    ) {
      return 'signup'
    } else {
      return 'myprofile'
    }
  }
}
